import type {
  AuthOrgOption,
  AuthResponse,
  ChangePasswordResponse,
  MyAccessRow,
  MyOrgsResponse,
  RegisterStatusResponse,
  RenameMeResponse,
  SessionsResponse,
  UserResponse,
} from '../types/api'
import { apiClient } from './client'

export async function register(
  email: string,
  username: string,
  password: string
): Promise<AuthResponse> {
  return apiClient.post<AuthResponse>('/auth/register', {
    email,
    username,
    password,
  })
}

export async function login(emailOrUsername: string, password: string): Promise<AuthResponse> {
  return apiClient.post<AuthResponse>('/auth/login', { emailOrUsername, password })
}

export async function selectOrg(selectToken: string, orgSlug: string): Promise<AuthResponse> {
  return apiClient.post<AuthResponse>('/auth/select-org', { selectToken, orgSlug })
}

export async function switchOrg(orgSlug: string): Promise<AuthResponse> {
  return apiClient.post<AuthResponse>('/auth/switch-org', { orgSlug })
}

export async function getMe(): Promise<UserResponse> {
  return apiClient.get<UserResponse>('/auth/me')
}

export async function getRegisterStatus(): Promise<RegisterStatusResponse> {
  return apiClient.get<RegisterStatusResponse>('/auth/register-status')
}

/**
 * Active orgs (slug + name only) for the bare-/login OAuth org picker.
 * Public pre-login surface; failure must hide the picker, never the page.
 */
/**
 * Orgs offered on the chooser screen. Gated on a short-lived select token and
 * scoped to that identity — this is not an organization directory, and there is
 * deliberately no unauthenticated endpoint that lists orgs.
 */
export async function fetchSelectOrgOptions(selectToken: string): Promise<AuthOrgOption[]> {
  const res = await apiClient.post<{ orgs: AuthOrgOption[] }>('/auth/select-org/options', {
    selectToken,
  })
  return res.orgs
}

export async function requestPasswordReset(email: string): Promise<{ message: string }> {
  return apiClient.post<{ message: string }>('/auth/forgot-password', { email })
}

export async function validateResetToken(token: string): Promise<{ valid: boolean }> {
  return apiClient.post<{ valid: boolean }>('/auth/validate-reset-token', { token })
}

export async function resetPassword(token: string, password: string): Promise<{ message: string }> {
  return apiClient.post<{ message: string }>('/auth/reset-password', { token, password })
}

export async function logout(): Promise<{ message: string }> {
  return apiClient.post<{ message: string }>('/auth/logout')
}

export async function logoutAll(): Promise<{ message: string }> {
  return apiClient.post<{ message: string }>('/auth/logout-all')
}

export async function getSessions(): Promise<SessionsResponse> {
  return apiClient.get<SessionsResponse>('/auth/sessions')
}

export async function revokeSession(sessionId: number): Promise<{ message: string }> {
  return apiClient.delete<{ message: string }>(`/auth/sessions/${sessionId}`)
}

export async function getMyOrgs(): Promise<MyOrgsResponse> {
  return apiClient.get<MyOrgsResponse>('/auth/my-orgs')
}

export async function changePassword(
  newPassword: string,
  currentPassword?: string
): Promise<ChangePasswordResponse> {
  return apiClient.post<ChangePasswordResponse>('/auth/change-password', {
    ...(currentPassword !== undefined && { currentPassword }),
    newPassword,
  })
}

export async function renameMe(username: string): Promise<RenameMeResponse> {
  return apiClient.patch<RenameMeResponse>('/auth/me', { username })
}

export async function getMyAccess(limit?: number): Promise<MyAccessRow[]> {
  const qs = limit != null ? `?limit=${limit}` : ''
  return apiClient.get<MyAccessRow[]>(`/auth/me/access${qs}`)
}
